import axios, { AxiosProgressEvent } from "axios";
import { TProjectData, TWellExaminer } from "../../types/project";
import {
	SharepointApi,
	SharepointListing,
	SharepointOwnerRow,
	SharepointRoot,
	SharepointSearchResponse,
} from "../../types/sharepointApi";
import { AuthHeader, url } from "../../utils/_exports";
import Emitter from "../../utils/emitter";

const apiURL = "well-examination";

const moduleUrl = (tenantKey: string, projectCode: string) =>
	`${url}/${tenantKey}/${apiURL}/${encodeURIComponent(projectCode)}`;

/** Surface the server's JSON body (status, reason, normalised error) to callers. */
const rejectWithBody = (error: any) =>
	Promise.reject(error?.response?.data || error);

export const wellExaminationPreviewPath = (
	projectCode: string,
	itemId: string,
	containerId: string
) =>
	`/projects/${encodeURIComponent(projectCode)}/well-examination/preview?record=${encodeURIComponent(itemId)}&container=${encodeURIComponent(containerId)}`;

/** SharepointApi backed by the project-scoped `/well-examination` module. */
export const createWellExaminationApi = (
	tenantKey: string,
	projectCode: string
): SharepointApi => {
	const base = moduleUrl(tenantKey, projectCode);

	const get = <T = any>(path: string, params?: Record<string, unknown>) =>
		axios
			.get<T>(`${base}${path}`, { ...AuthHeader, params })
			.then((response) => response.data)
			.catch(rejectWithBody);

	const send = <T = any>(
		method: "post" | "patch",
		path: string,
		body: Record<string, unknown>
	) =>
		axios[method]<T>(`${base}${path}`, body, AuthHeader)
			.then((response) => response.data)
			.catch(rejectWithBody);

	const toListing = (data: any): SharepointListing => ({
		items: data?.items || [],
		folderId: data?.folder_id ?? null,
		containerId: data?.container_id ?? null,
		owners: data?.owners || [],
	});

	return {
		getRoot: () =>
			get("/root").then((data): SharepointRoot => ({
				folderId: data?.folder_id ?? null,
				containerId: data?.container_id ?? null,
			})),
		listItems: (folderId) =>
			get("/items", folderId ? { folder_id: folderId } : undefined).then(
				toListing
			),
		getOwners: () =>
			get("/items").then((data): SharepointOwnerRow[] => data?.owners || []),
		createFolder: (name, folderId) =>
			send("post", "/folder", { name, folder_id: folderId }),
		uploadFiles: (files, folderId, _containerId, _hierarchy, socketId) => {
			const formData = new FormData();
			files.forEach((file) => formData.append("files", file));
			formData.append("folder_id", folderId);
			if (socketId) formData.append("socket_id", socketId);

			return axios
				.post(`${base}/upload`, formData, {
					...AuthHeader,
					onUploadProgress: (progressEvent: AxiosProgressEvent) => {
						if (progressEvent.lengthComputable) {
							Emitter.emit(
								"ACTION_PROGRESS",
								(((progressEvent.progress || 0) / 2) * 100).toFixed(0)
							);
						}
					},
				})
				.then((response) => response.data)
				.catch(rejectWithBody);
		},
		renameItem: (itemId, _containerId, newName) =>
			send("patch", "/rename", { item_id: itemId, new_name: newName }),
		deleteItems: (itemIds, _containerId, socketId) =>
			axios
				.delete(`${base}/items`, {
					...AuthHeader,
					data: { item_ids: itemIds, socket_id: socketId },
				})
				.then((response) => response.data)
				.catch(rejectWithBody),
		moveItems: (itemIds, moveToId) =>
			send("patch", "/move", { item_ids: itemIds, move_to_id: moveToId }),
		getFile: (itemId) => get(`/file/${encodeURIComponent(itemId)}`),
		getThumbnail: (itemId) => get(`/thumbnail/${encodeURIComponent(itemId)}`),
		getPreview: (itemId) =>
			send("post", `/preview/${encodeURIComponent(itemId)}`, {}),
		getDownloadUrl: (record) =>
			get(`/download/${encodeURIComponent(record.id)}`).then(
				(data) => data?.url || null
			),
		search: (query, limit = 20) =>
			get<SharepointSearchResponse>("/search", { q: query, limit }),
		collaborate: () =>
			Promise.reject(
				new Error(
					"Editing in Microsoft 365 is not available for well examination documents."
				)
			),
		previewPath: (itemId, containerId) =>
			wellExaminationPreviewPath(projectCode, itemId, containerId),
	};
};

/** Replace the examiner list of a Wells project. Returns the updated project. */
export const updateWellExaminers = (
	tenantKey: string,
	projectCode: string,
	wellExaminers: Pick<TWellExaminer, "user_id" | "email">[]
) =>
	axios
		.put<TProjectData>(
			`${url}/${tenantKey}/projects/well-examiners`,
			{ project_code: projectCode, well_examiners: wellExaminers },
			AuthHeader
		)
		.then((response) => response.data)
		.catch(rejectWithBody);
